import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GoalAnswer,
  OnboardingAnswers,
  createEmptyGoal
} from '../../utils/onboardingCategoryBuilder';
import {
  computeBudgetSummary,
  formatBudgetAmount
} from '../../utils/onboardingBudgetSummary';
import { fetchLatestExchangeRates } from '../../utils/exchangeRates';
import {
  AmountCurrencySelect,
  CardRemoveButton,
  RecurringFields,
  parseAmount
} from './OnboardingBuilderFields';

interface OnboardingGoalsStepProps {
  answers: OnboardingAnswers;
  budgetCurrency: string;
  onChange: (answers: OnboardingAnswers) => void;
}

const OnboardingGoalsStep: React.FC<OnboardingGoalsStepProps> = ({
  answers,
  budgetCurrency,
  onChange
}) => {
  const { t } = useTranslation();
  const defaultCurrency = answers.primaryCurrency || budgetCurrency || 'USD';
  const customGoals = answers.goals.filter(goal => !goal.isDefault);
  const defaultGoal = answers.goals.find(goal => goal.isDefault);
  const [exchangeRates, setExchangeRates] = useState<Awaited<
    ReturnType<typeof fetchLatestExchangeRates>
  > | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchLatestExchangeRates(budgetCurrency)
      .then(rates => {
        if (!cancelled) setExchangeRates(rates);
      })
      .catch(() => {
        if (!cancelled) setExchangeRates(null);
      });
    return () => {
      cancelled = true;
    };
  }, [budgetCurrency]);

  const budgetSummary = useMemo(
    () => computeBudgetSummary(answers, budgetCurrency, exchangeRates),
    [answers, budgetCurrency, exchangeRates]
  );

  const updateGoals = (goals: GoalAnswer[]) => {
    onChange({ ...answers, goals });
  };

  const updateGoalById = (goalId: string, patch: Partial<GoalAnswer>) => {
    updateGoals(answers.goals.map(goal => (goal.id === goalId ? { ...goal, ...patch } : goal)));
  };

  const removeGoal = (goalId: string) => {
    updateGoals(answers.goals.filter(goal => goal.id !== goalId));
  };

  const renderGoalCard = (goal: GoalAnswer, fromDefault: boolean) => (
    <div key={goal.id} className="builder-card onboarding-goal-card">
      <div className="builder-card-header">
        {fromDefault ? (
          <div className="goal-default-title">
            <strong>{goal.name || t('steps.goals.defaultSavingsName', { ns: 'onboarding' })}</strong>
            <span className="statement-account-type">
              {t('steps.goals.defaultSavingsBadge', { ns: 'onboarding' })}
            </span>
          </div>
        ) : (
          <input
            type="text"
            className="builder-text-input"
            value={goal.name}
            placeholder={t('steps.goals.namePlaceholder', { ns: 'onboarding' })}
            onChange={e => updateGoalById(goal.id, { name: e.target.value })}
          />
        )}
        {!fromDefault && (
          <CardRemoveButton
            onClick={() => removeGoal(goal.id)}
            label={t('builder.actions.remove', { ns: 'onboarding' })}
          />
        )}
      </div>

      <label className="builder-field">
        <span>{t('steps.goals.targetLabel', { ns: 'onboarding' })}</span>
        <p className="builder-help">{t('steps.goals.targetHelp', { ns: 'onboarding' })}</p>
        <div className="builder-amount-row">
          <input
            type="text"
            inputMode="decimal"
            className="builder-amount-input"
            value={goal.targetAmount != null ? String(goal.targetAmount) : ''}
            placeholder="0"
            onChange={e =>
              updateGoalById(goal.id, { targetAmount: parseAmount(e.target.value) })
            }
          />
          <AmountCurrencySelect
            value={goal.targetCurrency || defaultCurrency}
            onChange={currency => updateGoalById(goal.id, { targetCurrency: currency })}
          />
        </div>
      </label>

      {budgetSummary.canSaveForGoals && (
        <div className="goal-contribution-section">
          <p className="builder-sublabel">
            {t('steps.goals.contributionLabel', { ns: 'onboarding' })}
          </p>
          <p className="builder-help">{t('steps.goals.contributionHelp', { ns: 'onboarding' })}</p>
          <RecurringFields
            bill={goal.monthlyContribution}
            defaultCurrency={goal.targetCurrency || defaultCurrency}
            amountLabel={t('steps.goals.contributionAmount', { ns: 'onboarding' })}
            onChange={monthlyContribution => updateGoalById(goal.id, { monthlyContribution })}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="onboarding-goals-step">
      <section className="builder-section goals-snapshot">
        <h4>{t('steps.goals.snapshotTitle', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('steps.goals.snapshotHelp', { ns: 'onboarding' })}</p>
        <dl className="goals-snapshot-grid">
          <div>
            <dt>{t('steps.goals.snapshotIncome', { ns: 'onboarding' })}</dt>
            <dd>
              {formatBudgetAmount(budgetSummary.totalIncome)} {budgetSummary.currency}
            </dd>
          </div>
          <div>
            <dt>{t('steps.goals.snapshotCosts', { ns: 'onboarding' })}</dt>
            <dd>
              −{formatBudgetAmount(budgetSummary.totalCosts)} {budgetSummary.currency}
            </dd>
          </div>
          <div>
            <dt>{t('steps.goals.snapshotDebts', { ns: 'onboarding' })}</dt>
            <dd>
              −{formatBudgetAmount(budgetSummary.totalDebtPayments)} {budgetSummary.currency}
            </dd>
          </div>
          <div className="goals-snapshot-total">
            <dt>{t('steps.goals.snapshotRemaining', { ns: 'onboarding' })}</dt>
            <dd className={budgetSummary.canSaveForGoals ? 'positive' : 'negative'}>
              {formatBudgetAmount(budgetSummary.surplusAfterDebts)} {budgetSummary.currency}
            </dd>
          </div>
        </dl>
      </section>

      {!budgetSummary.canSaveForGoals && (
        <div className="info-box goals-debt-first-notice">
          <strong>{t('steps.goals.debtFirstTitle', { ns: 'onboarding' })}</strong>
          <p>{t('steps.goals.debtFirstBody', { ns: 'onboarding' })}</p>
          <p>{t('steps.goals.debtFirstEncouragement', { ns: 'onboarding' })}</p>
        </div>
      )}

      {budgetSummary.canSaveForGoals && (
        <div className="info-box goals-ready-notice">
          <strong>{t('steps.goals.readyTitle', { ns: 'onboarding' })}</strong>
          <p>
            {t('steps.goals.readyBody', {
              ns: 'onboarding',
              amount: formatBudgetAmount(budgetSummary.surplusAfterDebts),
              currency: budgetSummary.currency
            })}
          </p>
        </div>
      )}

      <section className="builder-section">
        <h4>{t('steps.goals.listTitle', { ns: 'onboarding' })}</h4>
        <p className="builder-help">{t('steps.goals.listHelp', { ns: 'onboarding' })}</p>
        {defaultGoal && renderGoalCard(defaultGoal, true)}
        {customGoals.map(goal => renderGoalCard(goal, false))}
        <button
          type="button"
          className="builder-add"
          onClick={() =>
            updateGoals([
              ...answers.goals,
              createEmptyGoal(customGoals.length, defaultCurrency)
            ])
          }
        >
          + {t('steps.goals.add', { ns: 'onboarding' })}
        </button>
      </section>
    </div>
  );
};

export default OnboardingGoalsStep;
